import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ChatContext = createContext(null);
const WS_URL = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/chat`;

// One live socket per signed-in tab: presence, new messages, typing and call signalling.
export function ChatProvider({ children }) {
    const { user } = useAuth();
    const [online, setOnline] = useState(new Set());
    const [unread, setUnread] = useState(0);
    const [connected, setConnected] = useState(false);
    const wsRef = useRef(null);
    const listeners = useRef(new Set());
    const callRef = useRef(null);

    const refreshUnread = useCallback(() => {
        api.get("/chat/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!user?.id) return undefined;
        let closed = false;
        let retry = 0;
        let timer;
        let ping;
        let opened = false;
        let lastSeen = Date.now();
        const connect = () => {
            clearTimeout(timer);
            const ws = new WebSocket(WS_URL);
            wsRef.current = ws;
            ws.onopen = () => {
                retry = 0;
                setConnected(true);
                if (opened) { refreshUnread(); listeners.current.forEach((fn) => fn({ type: "resync" })); }
                opened = true;
                lastSeen = Date.now();
                clearInterval(ping);
                ping = setInterval(() => {
                    if (ws.readyState !== 1) return;
                    if (Date.now() - lastSeen > 45000) ws.close();
                    else ws.send('{"type":"ping"}');
                }, 20000);
            };
            ws.onmessage = (e) => {
                lastSeen = Date.now();
                const m = JSON.parse(e.data);
                if (m.type === "hello") setOnline(new Set(m.online));
                if (m.type === "presence") setOnline((s) => { const n = new Set(s); m.online ? n.add(m.user_id) : n.delete(m.user_id); return n; });
                if (m.type === "message" && m.message.sender_id !== user.id) refreshUnread();
                listeners.current.forEach((fn) => fn(m));
            };
            ws.onclose = (e) => {
                if (wsRef.current !== ws) return;
                clearInterval(ping);
                setConnected(false);
                if (!closed && e.code !== 4401) timer = setTimeout(connect, Math.min(15000, 1000 * 2 ** retry++));
            };
        };
        // Phones drop sockets in the background: reconnect at once when the app is back or the network returns.
        const wake = () => {
            if (document.visibilityState !== "visible") return;
            const ws = wsRef.current;
            if (!ws || ws.readyState === 3) { retry = 0; connect(); }
            else if (ws.readyState === 1 && Date.now() - lastSeen > 30000) { retry = 0; ws.onclose = null; ws.close(); clearInterval(ping); connect(); }
            else if (ws.readyState === 1) ws.send('{"type":"ping"}');
        };
        document.addEventListener("visibilitychange", wake);
        window.addEventListener("online", wake);
        window.addEventListener("focus", wake);
        connect();
        refreshUnread();
        return () => {
            closed = true; clearTimeout(timer); clearInterval(ping); wsRef.current?.close();
            document.removeEventListener("visibilitychange", wake);
            window.removeEventListener("online", wake);
            window.removeEventListener("focus", wake);
        };
    }, [user?.id, refreshUnread]);

    const send = useCallback((obj) => {
        const ws = wsRef.current;
        if (ws?.readyState !== 1) return false;
        ws.send(JSON.stringify(obj));
        return true;
    }, []);
    const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
    const emit = useCallback((m) => listeners.current.forEach((fn) => fn(m)), []);
    const startCall = useCallback((peer, kind) => callRef.current?.(peer, kind), []);

    return (
        <ChatContext.Provider value={{ me: user, online, unread, connected, refreshUnread, send, subscribe, emit, startCall, callRef }}>
            {children}
        </ChatContext.Provider>
    );
}

export const useChat = () => useContext(ChatContext);
